import { Crown } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

interface FloatingProProps {
  isPaid: boolean;
  /** When true, shows a "Go Pro" call-to-action instead of the Pro crown. */
  showUpgradeForFree?: boolean;
}

/**
 * Floating Pro badge anchored to the left edge of the viewport, just below
 * the streak indicator. For Pro users this shows a crown that links to
 * subscription management; for free users (when enabled) it prompts upgrade.
 */
export function FloatingPro({ isPaid, showUpgradeForFree = true }: FloatingProProps) {
  const { t } = useTranslation();
  if (!isPaid && !showUpgradeForFree) return null;

  const to = isPaid ? "/subscription" : "/upgrade";
  const label = isPaid ? t("common.pro", { defaultValue: "Pro" }) : t("home.upgradeToProLink", { defaultValue: "Go Pro" });
  const gradient = isPaid
    ? "bg-gradient-to-br from-neon-yellow to-neon-pink text-white shadow-neon-pink/40 ring-white/40 dark:ring-white/10"
    : "bg-card text-ink border border-rule shadow-black/10 ring-rule";

  return (
    <div
      className="fixed left-3 sm:left-4 top-[calc(50%+3.5rem)] z-40 pointer-events-none"
      aria-live="polite"
    >
      <Link
        to={to}
        aria-label={isPaid ? "Manage Pro subscription" : "Upgrade to Pro"}
        className={`pointer-events-auto flex flex-col items-center gap-1 rounded-full px-2.5 py-3 shadow-lg ring-2 animate-in fade-in slide-in-from-left-4 ${gradient}`}
      >
        <Crown className="size-5 sm:size-6" aria-hidden="true" />
        <span className="text-[10px] sm:text-xs font-black leading-none uppercase tracking-wider">
          {label}
        </span>
      </Link>
    </div>
  );
}